import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { loginWatcher, setEmail } from "../../redux/actions/loginactioncreators";
import { TextField } from "../fields/TextField";
import { FormattedMessage, useIntl } from "react-intl";
import flowStages from "../../flowStages";
import { NewFADVLoginForm } from "../login/NewFADVLoginForm";
import { navigateToLogin } from "../../redux/helpers/navigators";
import { mfaToastMessages } from "../../i18n/mfaToastMessages.js";
import { enqueueSnackbar } from "notistack";

export const ConfirmEmailAddress = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [resendRequested, setResendRequested] = useState(false);
  const username = useSelector(state => state.loginForm.username);
  const password = useSelector(state => state.loginForm.password);
  const challenge = useSelector(state => state.loginForm.challenge);
  const userAttributes = useSelector(state => state.loginForm.userInfo?.UserAttributes);
  const email = userAttributes?.email ?? "";
  const isSignUpFlow = userAttributes?.["custom:migrated:type"]?.toLowerCase() === "signup";
  const destination = isSignUpFlow ? email : challenge?.challengeParameters?.["DESTINATION"];
  const login = isSignUpFlow ? email : username;

  useEffect(() => {
    if (resendRequested && challenge?.challengeName === flowStages.VERIFY_EMAIL) {
      enqueueSnackbar(intl.formatMessage(mfaToastMessages.emailVerificationCodeSent), { variant: "success" });
      setResendRequested(false);
    }
  }, [resendRequested, challenge, intl]);

  const handleSubmit = (formData, _errors) => {
    dispatch(setEmail(email));
    const code = formData.confirmEmailAddress;
    const challengeUsername = isSignUpFlow ? email : challenge.challengeParameters.USER_ID_FOR_SRP;
    dispatch(loginWatcher({
      flowStage: flowStages.VERIFY_EMAIL,
      username: login,
      password,
      flowParameter: isSignUpFlow ? "signup" : "signin",
      challengeResponse: {
        session: challenge.session,
        challengeName: challenge.challengeName,
        challengeParameters: {
          USERNAME: challengeUsername,
          CODE: code,
        }
      }
    }));
  };

  const handleResendCode = () => {
    const challengeUsername = isSignUpFlow ? email : challenge?.challengeParameters?.USER_ID_FOR_SRP;
    setResendRequested(true);
    dispatch(loginWatcher({
      flowStage: flowStages.RESEND_EMAIL_VERIFICATION_CODE,
      challengeResponse: {
        session: challenge.session,
        challengeName: challenge.challengeName,
        challengeParameters: {
          USERNAME: challengeUsername,
          RESEND: "true",
        }
      }
    }));
  };

  const handleReturnToSignIn = () => {
    navigateToLogin();
  };

  return (
    <NewFADVLoginForm
      requiredFields={["confirmEmailAddress"]}
      onSubmit={handleSubmit}
      secondaryButtonText={
        <FormattedMessage
          id="mfa.resendOtpButton"
          defaultMessage="Resend Code"
          description="Link text for resending email verification code"
        />
      }
      secondaryButtonHandler={handleResendCode}
      secondaryButtonId="resendCode"
      submitButtonText={
        <FormattedMessage
          id="login.confirmEmailSubmit"
          defaultMessage="Confirm Email"
          description="Button text for submitting email confirmation code"
        />
      }
      middleContent={(
        <div className="flex flex-col justify-center items-center">
          <button
            type="button"
            id="returnToSignIn"
            className="w-full bg-white hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-colors duration-200"
            onClick={handleReturnToSignIn}
          >
            <FormattedMessage id="signup.returnToSignIn" defaultMessage="Return to Sign In" description="Button text to return user from email confirmation flow to login form" />
          </button>
        </div>
      )}
    >
      <div className="text-base">
        <FormattedMessage
          id="login.confirmEmailBlurb"
          defaultMessage="An email was sent to {destination} with a verification code. Please type it into the input below to confirm your email address."
          description="Blurb shown to user explaining that a verification code was sent to their email address."
          values={{ destination }}
        />
      </div>
      <TextField
        id="confirmEmailAddress"
        name="confirmEmailAddress"
        autoFocus={true}
        label={
          <FormattedMessage
            id="login.emailAddressConfirmationCode"
            defaultMessage="Confirmation Code"
            description="Label for the confirmation code input field in email verification form"
          />
        }
      />
    </NewFADVLoginForm>
  );
};
